import { InjectionToken, Signal, signal } from '@angular/core';

/**
 * Capability flags consumed by the comments UI to gate moderator and authoring actions. Mirrors
 * the document-management capability pattern: the lib itself doesn't depend on the host app's
 * auth machinery — the host provides an implementation via {@link COMMENTS_CAPABILITIES}, and the
 * lib defaults to "fully capable" so component-level usage in tests and isolated dev hosts works
 * without setup.
 *
 * - `canCreateComments`: maps to `comments.create` — required to post a new comment or reply.
 *   Gates the "+ New comment" header button and the per-row Reply button.
 * - `canModerateComments`: maps to `comments.moderate` — required to edit any comment, delete
 *   any comment, restore a tombstoned comment, and view edit history. Author actions on the
 *   user's own comments do not require this grant; the row menu performs the author-id check
 *   client-side and shows Edit/Delete on own comments regardless of this flag.
 * - `currentUserId`: id of the authenticated user, or `null` when unknown. Used by the row menu
 *   to decide author-vs-moderator gating: a comment whose `author.id === currentUserId` shows
 *   Edit/Delete unconditionally; everyone else's comments require {@link canModerateComments}.
 *   Defaults to `null` in the fallback (every action treated as a moderator action) so isolated
 *   tests/dev hosts can exercise the moderator path without wiring up auth.
 */
export interface CommentsCapabilities {
  readonly canCreateComments: Signal<boolean>;
  readonly canModerateComments: Signal<boolean>;
  readonly currentUserId: Signal<string | null>;
}

const FULL_CAPABILITIES: CommentsCapabilities = {
  canCreateComments: signal(true).asReadonly(),
  canModerateComments: signal(true).asReadonly(),
  currentUserId: signal<string | null>(null).asReadonly(),
};

export const COMMENTS_CAPABILITIES = new InjectionToken<CommentsCapabilities>(
  'CommentsCapabilities',
  {
    providedIn: 'root',
    factory: () => FULL_CAPABILITIES,
  },
);
